import { Link, useParams } from "react-router-dom";
import {
  provideDownloadOrderReceiptUseCase,
  provideGetOrderByIdUseCase,
  provideUpdateOrderUseCase,
} from "../../../di/UseCasesModule";
import { OrderDetailsViewModel } from "./OrderDetailsViewModel";
import {
  Box,
  Button,
  capitalize,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
} from "@mui/material";
import { LoadingView } from "../../../component/LoadingView";
import { HeaderView } from "../../../component/HeaderView";
import Marquee from "react-fast-marquee";
import { CustomTextView } from "../../../component/CustomTextView";
import {
  COLOR_BLACK,
  COLOR_GREEN,
  COLOR_LESS_BLACK,
  COLOR_LIGHT_GRAY,
  COLOR_ORANGE,
  COLOR_RED,
  COLOR_WHITE,
} from "../../../../data/colors/Colors";
import { CustomerInfoView } from "../../customers/details/CustomerDetailsView";
import { TitleView } from "../../../component/TitleView";
import { MAIN_FONT_FAMILY } from "../../../../data/constant/Constants";
import { Order, OrderStatus } from "../../../../domain/model/Order";
import { CustomCardView } from "../../../component/CustomCardView";
import moment from "moment";
import { ROUTE_ENDPOINTS } from "../../../../data/constant/RouteConstants";
import { CustomImageView } from "../../../component/CustomImageView";
import {
  CancelRounded,
  CheckBox,
  CheckCircleRounded,
  CheckRounded,
  DownloadRounded,
  PendingActionsRounded,
} from "@mui/icons-material";

export function OrderDetailsView() {
  const { id } = useParams();
  if (!id) return <></>;

  const viewModel = OrderDetailsViewModel(
    id,
    provideGetOrderByIdUseCase(),
    provideUpdateOrderUseCase(),
    provideDownloadOrderReceiptUseCase()
  );
  const order = viewModel.order;
  return (
    <Box>
      <Stack>
        {viewModel.isLoading ? (
          <LoadingView />
        ) : (
          <>
            <Stack>
              <HeaderView
                title={"Order"}
                alignItems="start"
                titleLayout={
                  <>
                    {order?._id && (
                      <Marquee style={{ width: 100 }} delay={2} pauseOnHover>
                        <CustomTextView
                          text={`#${order._id}`}
                          fontWeight="300"
                          variant="h6"
                          textColor={COLOR_LIGHT_GRAY}
                        />
                      </Marquee>
                    )}
                  </>
                }
                layout={<></>}
              />

              {order && (
                <Grid container pl={3} pr={3} pb={3} pt={0} spacing={3}>
                  <OrderView
                    order={order}
                    onStatusChanged={(id, status) => {
                      viewModel.updateOrder(id, { status });
                    }}
                    downloadReceipt={(id) => {
                      viewModel.downloadReceipt(id);
                    }}
                  />
                  {order.customer && (
                    <>
                      <Grid size={12}>
                        <TitleView size="h6" title="Ordered By" />
                      </Grid>
                      <CustomerInfoView customer={order.customer} />
                    </>
                  )}
                </Grid>
              )}
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  );
}

function OrderView({
  order,
  downloadReceipt,
  onStatusChanged,
}: {
  order: Order;
  onStatusChanged: (orderId: string, status: OrderStatus) => void;
  downloadReceipt: (orderId: string) => void;
}) {
  return (
    <>
      <Grid size={{ sm: 12, xs: 12, md: 12, lg: 6, xl: 8 }}>
        <CustomCardView
          padding={2}
          children={
            <Stack justifyContent={"space-between"} minHeight={"50vh"}>
              <Stack spacing={0.5}>
                <CustomTextView text="Price" textColor={COLOR_LIGHT_GRAY} />
                <CustomTextView
                  text={`${order.transaction.priceInQAR.toFixed(2)} QR`}
                  fontSize={32}
                  fontWeight="bold"
                />
              </Stack>

              <Stack spacing={0.5}>
                <CustomTextView text="Quantity" textColor={COLOR_LIGHT_GRAY} />
                <CustomTextView
                  text={`${order.qty}`}
                  fontSize={32}
                  fontWeight="bold"
                />
              </Stack>

              <Stack spacing={0.5}>
                <CustomTextView text="Product" textColor={COLOR_LIGHT_GRAY} />
                {order.product ? (
                  <Stack alignItems={"center"} spacing={1}>
                    <CustomImageView
                      width={120}
                      height={120}
                      src={
                        (
                          order.product.imagesList![
                            order.product.thumbnailIndex!
                          ] as any
                        ).downloadUrl
                      }
                      scale="contain"
                      props={{
                        borderRadius: 4,
                        background: COLOR_WHITE,
                      }}
                    />
                    <Link
                      to={
                        ROUTE_ENDPOINTS.HOME_PRODUCTS + "/" + order.product._id
                      }
                      target="_blank"
                    >
                      <CustomTextView
                        text={order.product.name}
                        fontSize={22}
                        textAlign="center"
                      />
                    </Link>
                  </Stack>
                ) : (
                  <CustomTextView
                    text="REMOVED"
                    textColor="red"
                    textAlign="center"
                  />
                )}
              </Stack>
            </Stack>
          }
        />
      </Grid>
      <Grid size={{ sm: 12, xs: 12, md: 12, lg: 6, xl: 4 }}>
        <CustomCardView
          padding={2}
          children={
            <Stack justifyContent={"space-between"} minHeight={"30vh"}>
              <Stack spacing={0.5}>
                <CustomTextView text="Status" textColor={COLOR_LIGHT_GRAY} />
                <Select
                  sx={{
                    fontFamily: MAIN_FONT_FAMILY,
                    borderRadius: 4,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor:
                        order.status == OrderStatus.CONFIRMED
                          ? COLOR_GREEN
                          : order.status == OrderStatus.PENDING
                          ? COLOR_ORANGE
                          : COLOR_RED,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: COLOR_BLACK,
                    },
                    "& .MuiSvgIcon-root": {
                      color:
                        order.status == OrderStatus.CONFIRMED
                          ? COLOR_GREEN
                          : order.status == OrderStatus.PENDING
                          ? COLOR_ORANGE
                          : COLOR_RED,
                    },
                  }}
                  inputProps={{
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          background: COLOR_WHITE,
                        },
                      },
                    },
                  }}
                  value={order.status}
                  onChange={(e: SelectChangeEvent<string>) => {
                    if (e.target.value == order.status) return;
                    onStatusChanged(order._id, e.target.value as OrderStatus);
                  }}
                  renderValue={(val) => (
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      spacing={0.5}
                      width={"100%"}
                    >
                      {order.status == OrderStatus.CONFIRMED && (
                        <CheckCircleRounded
                          fontSize="small"
                          sx={{ color: COLOR_GREEN }}
                        />
                      )}
                      {order.status == OrderStatus.PENDING && (
                        <PendingActionsRounded
                          fontSize="small"
                          sx={{ color: "orange" }}
                        />
                      )}
                      {order.status == OrderStatus.CANCELLED && (
                        <CancelRounded fontSize="small" sx={{ color: "red" }} />
                      )}
                      <CustomTextView
                        text={capitalize(val as string)}
                        variant="body2"
                        textColor={
                          order.status === OrderStatus.CONFIRMED
                            ? COLOR_GREEN
                            : order.status === OrderStatus.PENDING
                            ? COLOR_ORANGE
                            : COLOR_RED
                        }
                      />
                    </Stack>
                  )}
                >
                  {Object.values(OrderStatus).map((status, index) => {
                    return (
                      <MenuItem
                        key={index}
                        sx={{
                          fontFamily: MAIN_FONT_FAMILY,
                        }}
                        value={status}
                      >
                        <Stack
                          direction={"row"}
                          alignItems={"center"}
                          spacing={0.5}
                        >
                          {status == OrderStatus.CONFIRMED && (
                            <CheckCircleRounded
                              fontSize="small"
                              sx={{ color: COLOR_GREEN }}
                            />
                          )}
                          {status == OrderStatus.PENDING && (
                            <PendingActionsRounded
                              fontSize="small"
                              sx={{ color: "orange" }}
                            />
                          )}
                          {status == OrderStatus.CANCELLED && (
                            <CancelRounded
                              fontSize="small"
                              sx={{ color: "red" }}
                            />
                          )}
                          <CustomTextView text={capitalize(status)} />
                        </Stack>
                      </MenuItem>
                    );
                  })}
                </Select>
              </Stack>

              <Stack spacing={0.5}>
                <CustomTextView text="Date" textColor={COLOR_LIGHT_GRAY} />
                <CustomTextView
                  text={moment(new Date(order.createdAt)).format(
                    "YYYY-MM-DD HH:mm:ss"
                  )}
                  fontSize={20}
                  textAlign="center"
                />
              </Stack>
              <Stack spacing={0.5}>
                <CustomTextView text="Receipt" textColor={COLOR_LIGHT_GRAY} />
                <Button
                  sx={{
                    textTransform: "none",
                    borderRadius: 22,
                    borderColor: COLOR_BLACK,
                  }}
                  variant="outlined"
                  onClick={() => {
                    downloadReceipt(order.orderId.toString());
                  }}
                >
                  <DownloadRounded sx={{ color: COLOR_BLACK }} />
                  <CustomTextView text="Download Receipt" variant="h6" />
                </Button>
              </Stack>
            </Stack>
          }
        />
      </Grid>
    </>
  );
}
